import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';
import {
  FiBarChart2, FiTrendingUp, FiTrendingDown, FiActivity,
  FiUsers, FiTruck, FiDollarSign, FiMapPin
} from 'react-icons/fi';
import UsageZoneMap from './UsageZoneMap';

function AdminAnalytics() {
  const [stats, setStats] = useState(null);
  const [zoneStats, setZoneStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoneLoading, setZoneLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d'); // 7d, 30d, 90d, 1y

  useEffect(() => {
    loadAnalytics();
    loadZoneStats();
  }, [dateRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.getCompleteAppStats();
      if (response && response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des analyses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadZoneStats = async () => {
    try {
      setZoneLoading(true);
      const response = await api.getUsageByZone(dateRange);
      if (response?.success) {
        setZoneStats(response.data);
      }
    } catch (error) {
      console.error('Erreur zones d\'utilisation:', error);
      setZoneStats(null);
    } finally {
      setZoneLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  // Graphique de conversion
  const conversionData = stats && stats.conversion ? {
    labels: ['Devis créés', 'Acceptés', 'Missions terminées', 'Annulés'],
    datasets: [{
      label: 'Conversion',
      data: [
        stats.conversion.totalQuotes || 0,
        stats.conversion.acceptedQuotes || 0,
        stats.conversion.completedQuotes || 0,
        stats.conversion.rejectedQuotes || 0
      ],
      backgroundColor: [
        'rgba(234, 179, 8, 0.6)',
        'rgba(59, 130, 246, 0.6)',
        'rgba(34, 197, 94, 0.6)',
        'rgba(239, 68, 68, 0.6)'
      ],
      borderColor: [
        'rgba(234, 179, 8, 1)',
        'rgba(59, 130, 246, 1)',
        'rgba(34, 197, 94, 1)',
        'rgba(239, 68, 68, 1)'
      ],
      borderWidth: 2
    }]
  } : null;

  // Graphique des tickets par type
  const ticketsByTypeData = stats && stats.tickets ? {
    labels: ['Qualité', 'Technique', 'Paiement'],
    datasets: [{
      label: 'Tickets',
      data: [
        stats.tickets.byType?.quality || 0,
        stats.tickets.byType?.technical || 0,
        stats.tickets.byType?.payment || 0
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.6)',
        'rgba(168, 85, 247, 0.6)',
        'rgba(34, 197, 94, 0.6)'
      ],
      borderColor: [
        'rgba(59, 130, 246, 1)',
        'rgba(168, 85, 247, 1)',
        'rgba(34, 197, 94, 1)'
      ],
      borderWidth: 2
    }]
  } : null;

  const topZones = (zoneStats?.byZone || []).slice(0, 12);
  const zoneBarData = topZones.length
    ? {
        labels: topZones.map((z) => z.zone),
        datasets: [
          {
            label: 'Clients',
            data: topZones.map((z) => z.client || 0),
            backgroundColor: 'rgba(59, 130, 246, 0.85)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 1,
          },
          {
            label: 'Déménageurs',
            data: topZones.map((z) => z.demenageur || 0),
            backgroundColor: 'rgba(234, 179, 8, 0.85)',
            borderColor: 'rgba(234, 179, 8, 1)',
            borderWidth: 1,
          },
        ],
      }
    : null;

  const moverServiceData =
    zoneStats?.byMoverServiceType?.length
      ? {
          labels: zoneStats.byMoverServiceType.map((s) => s.label),
          datasets: [
            {
              label: 'Activité déménageurs',
              data: zoneStats.byMoverServiceType.map((s) => s.count),
              backgroundColor: [
                'rgba(234, 179, 8, 0.75)',
                'rgba(59, 130, 246, 0.75)',
                'rgba(34, 197, 94, 0.75)',
                'rgba(168, 85, 247, 0.75)',
                'rgba(239, 68, 68, 0.75)',
                'rgba(107, 114, 128, 0.75)',
              ],
              borderWidth: 1,
            },
          ],
        }
      : null;

  const zoneBarOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: { color: '#9ca3af' },
      },
      tooltip: {
        backgroundColor: 'rgba(31, 41, 55, 0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { color: 'rgba(107, 114, 128, 0.2)' },
        ticks: { color: '#9ca3af', stepSize: 1 },
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: { color: '#e5e7eb', font: { size: 11 } },
      },
    },
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#9ca3af'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(31, 41, 55, 0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(234, 179, 8, 0.5)',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(107, 114, 128, 0.2)' },
        ticks: { color: '#9ca3af' }
      },
      y: {
        grid: { color: 'rgba(107, 114, 128, 0.2)' },
        ticks: { color: '#9ca3af' }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-4">Chargement des analyses...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Impossible de charger les données</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-yellow-400 mb-2">Analyses & Statistiques</h1>
          <p className="text-gray-400">Vue détaillée des performances de la plateforme</p>
        </div>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
        >
          <option value="7d">7 derniers jours</option>
          <option value="30d">30 derniers jours</option>
          <option value="90d">90 derniers jours</option>
          <option value="1y">1 an</option>
        </select>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <FiTrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <span className="text-green-400 text-sm font-semibold">
              {stats.conversion?.acceptanceRate || 0}%
            </span>
          </div>
          <p className="text-gray-400 text-sm mb-1">Taux d'acceptation</p>
          <p className="text-2xl font-bold text-white">
            {formatNumber(stats.conversion?.acceptedQuotes || 0)} / {formatNumber(stats.conversion?.totalQuotes || 0)}
          </p>
        </div>

        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <FiDollarSign className="w-6 h-6 text-blue-400" />
            </div>
            <span className="text-blue-400 text-sm font-semibold">
              {stats.conversion?.missionsCompletionRate || 0}%
            </span>
          </div>
          <p className="text-gray-400 text-sm mb-1">Taux des missions terminées</p>
          <p className="text-2xl font-bold text-white">
            {formatNumber(stats.conversion?.completedQuotes || 0)} / {formatNumber(stats.conversion?.totalQuotes || 0)}
          </p>
        </div>

        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <FiUsers className="w-6 h-6 text-yellow-400" />
            </div>
            <span className="text-yellow-400 text-sm font-semibold">
              {stats.messages?.activeSenders || 0}
            </span>
          </div>
          <p className="text-gray-400 text-sm mb-1">Utilisateurs actifs</p>
          <p className="text-2xl font-bold text-white">
            {formatNumber(stats.messages?.total || 0)} messages
          </p>
        </div>

        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-500/20 rounded-lg">
              <FiActivity className="w-6 h-6 text-purple-400" />
            </div>
            <span className="text-purple-400 text-sm font-semibold">
              {stats.payments?.successful || 0}
            </span>
          </div>
          <p className="text-gray-400 text-sm mb-1">Paiements réussis</p>
          <p className="text-2xl font-bold text-white">
            {formatCurrency(stats.payments?.totalPaid || 0)}
          </p>
        </div>
      </div>

      {/* Zones d'utilisation */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h2 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
              <FiMapPin className="w-5 h-5" />
              Utilisation globale de l&apos;app par zone
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              Position, messages et connexions — clients vs déménageurs (pas les adresses de devis)
            </p>
          </div>
          {!zoneLoading && zoneStats && (
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-300 border border-blue-500/40">
                Clients : {zoneStats.byRole?.client ?? 0} actions
              </span>
              <span className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/40">
                Déménageurs : {zoneStats.byRole?.demenageur ?? 0} actions
              </span>
              <span className="text-gray-500">
                {zoneStats.totalActivityEvents ?? 0} événements · {zoneStats.totalGpsPoints ?? 0} points GPS
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
            <h3 className="text-lg font-bold text-white mb-4">Zones les plus actives</h3>
            <div className="h-80">
              {zoneLoading ? (
                <p className="text-gray-400 text-center py-16">Chargement...</p>
              ) : zoneBarData ? (
                <Bar data={zoneBarData} options={zoneBarOptions} />
              ) : (
                <p className="text-gray-400 text-center py-16">Aucune activité sur cette période</p>
              )}
            </div>
          </div>

          
          <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
            <h3 className="text-lg font-bold text-white mb-4">Types de service (déménageurs)</h3>
            <div className="h-80">
              {zoneLoading ? (
                <p className="text-gray-400 text-center py-16">Chargement...</p>
              ) : moverServiceData ? (
                <Doughnut
                  data={moverServiceData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: { color: '#9ca3af', boxWidth: 12 },
                      },
                    },
                  }}
                />
              ) : (
                <p className="text-gray-400 text-center py-16">
                  Aucune activité déménageur typée sur cette période
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <h3 className="text-lg font-bold text-white mb-2">Carte d&apos;activité (GPS)</h3>
          <p className="text-gray-500 text-xs mb-4">
            <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1 align-middle" />
            Client
            <span className="inline-block w-3 h-3 rounded-full bg-yellow-500 ml-4 mr-1 align-middle" />
            Déménageur
          </p>
          <UsageZoneMap heatmap={zoneStats?.heatmap} loading={zoneLoading} />
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel de conversion */}
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center">
            <FiBarChart2 className="mr-2" /> Funnel de conversion
          </h3>
          <div className="h-80">
            {conversionData && <Bar data={conversionData} options={chartOptions} />}
          </div>
        </div>

        {/* Tickets par type */}
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center">
            <FiActivity className="mr-2" /> Répartition des tickets
          </h3>
          <div className="h-80">
            {ticketsByTypeData && <Doughnut data={ticketsByTypeData} options={chartOptions} />}
          </div>
        </div>

        {/* Top déménageurs */}
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6 lg:col-span-2">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center">
            <FiTruck className="mr-2" /> Top 10 déménageurs par revenus
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Rang</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Déménageur</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Devis</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Terminées</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Revenus</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Note</th>
                </tr>
              </thead>
              <tbody>
                {(stats.topMovers || []).slice(0, 10).map((mover, index) => (
                  <tr key={mover.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-white font-semibold">#{index + 1}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-medium">{mover.firstName} {mover.lastName}</p>
                        <p className="text-gray-400 text-sm">{mover.companyName || mover.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white">{mover.totalQuotes}</td>
                    <td className="px-4 py-3 text-green-400 font-semibold">{mover.completedQuotes ?? 0}</td>
                    <td className="px-4 py-3 text-yellow-400 font-bold">{formatCurrency(mover.totalRevenue)}</td>
                    <td className="px-4 py-3">
                      {mover.rating ? (
                        <span className="text-yellow-400 font-semibold">{mover.rating.toFixed(1)}/5</span>
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Statistiques détaillées */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <h4 className="text-lg font-bold text-white mb-4">Messages</h4>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Total</span>
              <span className="text-white font-semibold">{formatNumber(stats.messages?.total || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Dernières 24h</span>
              <span className="text-blue-400 font-semibold">{formatNumber(stats.messages?.last24h || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Non lus</span>
              <span className="text-red-400 font-semibold">{formatNumber(stats.messages?.unread || 0)}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <h4 className="text-lg font-bold text-white mb-4">Paiements</h4>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Total payé</span>
              <span className="text-green-400 font-semibold">{formatCurrency(stats.payments?.totalPaid || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Paiement moyen</span>
              <span className="text-white font-semibold">{formatCurrency(stats.payments?.avgPayment || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Taux de réussite</span>
              <span className="text-green-400 font-semibold">
                {stats.payments && stats.payments.total > 0 
                  ? ((stats.payments.successful / stats.payments.total) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <h4 className="text-lg font-bold text-white mb-4">Tickets</h4>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Total</span>
              <span className="text-white font-semibold">{formatNumber(stats.tickets?.total || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Ouverts</span>
              <span className="text-yellow-400 font-semibold">{formatNumber(stats.tickets?.open || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Résolus</span>
              <span className="text-green-400 font-semibold">{formatNumber(stats.tickets?.resolved || 0)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAnalytics;

